// Drop-in replacement for react-toastify's `toast` API, rendered by <AppToastHost />.
//
// Supported: toast(msg, opts), toast.success / error / info / warn / warning / loading / dark,
// toast.dismiss(id?), toast.update(id, opts), toast.promise(p, msgs, opts), toast.isActive(id).
// Options: type, autoClose (ms | false), toastId, title, position, onClose, onOpen, closeButton,
// hideProgressBar, pauseOnHover, closeOnClick, icon (node | false), className, data,
// action ({ label, onClick, dismissOnClick }).

import React from "react";
import * as store from "./toastStore";

export const TYPE = {
  DEFAULT: "default",
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
  LOADING: "loading",
};

export const POSITION = {
  TOP_CENTER: "top-center",
  TOP_RIGHT: "top-right",
  TOP_LEFT: "top-left",
  BOTTOM_CENTER: "bottom-center",
  BOTTOM_RIGHT: "bottom-right",
  BOTTOM_LEFT: "bottom-left",
};

const DEFAULT_AUTOCLOSE = {
  default: 3500,
  success: 3200,
  info: 3500,
  warning: 4500,
  error: 5500,
  loading: false,
};

const GENERIC_ERROR = "Something went wrong. Please try again.";

const defaults = {
  position: POSITION.TOP_CENTER,
  limit: 5,
};

export const configure = (next = {}) => {
  if (next.position && Object.values(POSITION).includes(next.position)) defaults.position = next.position;
  if (Number.isFinite(next.limit) && next.limit > 0) defaults.limit = next.limit;
};

const normalizeType = (type) => {
  if (type === "warn" || type === "warning") return TYPE.WARNING;
  if (type === "dark") return TYPE.DEFAULT;
  return Object.values(TYPE).includes(type) ? type : TYPE.DEFAULT;
};

const normalizePosition = (position) =>
  Object.values(POSITION).includes(position) ? position : defaults.position;

const normalizeAutoClose = (value, type) => {
  const fallback = DEFAULT_AUTOCLOSE[type];
  if (value === undefined || value === null || value === true) return fallback;
  if (value === false) return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

// Turn whatever callers pass into something renderable. Mirrors react-toastify's leniency
// but also unwraps Error objects / API error payloads instead of silently dropping them.
export const toRenderable = (content, type) => {
  const fallback = type === TYPE.ERROR ? GENERIC_ERROR : null;
  if (content === undefined || content === null) return fallback;
  if (React.isValidElement(content) || typeof content === "function") return content;
  if (typeof content === "string") return content.trim() ? content : fallback;
  if (typeof content === "number" || typeof content === "boolean") return String(content);
  if (content instanceof Error) return content.message || fallback;
  if (typeof content === "object") {
    const candidates = [
      content.message,
      content.error,
      content.msg,
      content.detail,
      content.data?.message,
      content.response?.data?.message,
    ];
    const hit = candidates.find((c) => typeof c === "string" && c.trim());
    if (hit) return hit;
    const nested = candidates.find((c) => c && typeof c === "object" && typeof c.message === "string");
    if (nested) return nested.message;
    return fallback;
  }
  return String(content);
};

const buildSpec = (content, type, options) => {
  const opts = options && typeof options === "object" ? options : {};
  const resolvedType = normalizeType(opts.type || type);
  return {
    type: resolvedType,
    content: toRenderable(opts.render !== undefined ? opts.render : content, resolvedType),
    title: typeof opts.title === "string" && opts.title.trim() ? opts.title : null,
    autoClose: normalizeAutoClose(opts.autoClose, resolvedType),
    position: normalizePosition(opts.position),
    closeButton: opts.closeButton !== false,
    hideProgressBar: !!opts.hideProgressBar,
    pauseOnHover: opts.pauseOnHover !== false,
    closeOnClick: opts.closeOnClick !== false && !opts.action,
    icon: opts.icon,
    className: typeof opts.className === "string" ? opts.className : "",
    onClose: typeof opts.onClose === "function" ? opts.onClose : null,
    onOpen: typeof opts.onOpen === "function" ? opts.onOpen : null,
    action:
      opts.action && typeof opts.action === "object" && opts.action.label ? opts.action : null,
    data: opts.data,
  };
};

const enforceLimit = (position) => {
  const open = store.getState().filter((t) => t.position === position && !t.closing);
  const overflow = open.length - defaults.limit;
  if (overflow <= 0) return;
  open.slice(0, overflow).forEach((t) => store.patch(t.id, { closing: true }));
};

const show = (content, type, options) => {
  const spec = buildSpec(content, type, options);
  if (spec.content === null) return null;

  const explicitId =
    options && options.toastId !== undefined && options.toastId !== null ? String(options.toastId) : null;

  if (explicitId) {
    const existing = store.find(explicitId);
    if (existing && !existing.closing) {
      store.patch(explicitId, (t) => ({ ...spec, nonce: t.nonce + 1, updatedAt: Date.now() }));
      return explicitId;
    }
  }

  // Collapse exact duplicates (same tone + text) into one card with a counter.
  if (typeof spec.content === "string") {
    const dup = store
      .getState()
      .find(
        (t) =>
          !t.closing &&
          t.type === spec.type &&
          t.position === spec.position &&
          typeof t.content === "string" &&
          t.content === spec.content
      );
    if (dup) {
      store.patch(dup.id, (t) => ({ count: (t.count || 1) + 1, nonce: t.nonce + 1, updatedAt: Date.now() }));
      return dup.id;
    }
  }

  const id = explicitId || store.nextId();
  store.push({
    id,
    ...spec,
    count: 1,
    nonce: 0,
    closing: false,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  enforceLimit(spec.position);
  if (spec.onOpen) {
    try {
      spec.onOpen(spec.data);
    } catch (e) {
      /* ignore */
    }
  }
  return id;
};

const toast = (content, options) => show(content, TYPE.DEFAULT, options);

toast.success = (content, options) => show(content, TYPE.SUCCESS, options);
toast.error = (content, options) => show(content, TYPE.ERROR, options);
toast.info = (content, options) => show(content, TYPE.INFO, options);
toast.warning = (content, options) => show(content, TYPE.WARNING, options);
toast.warn = toast.warning;
toast.loading = (content, options) =>
  show(content, TYPE.LOADING, { closeButton: false, closeOnClick: false, ...(options || {}) });
toast.dark = (content, options) => show(content, TYPE.DEFAULT, options);

toast.dismiss = (id) => {
  if (id === undefined || id === null) {
    store.patchAll({ closing: true });
    return;
  }
  store.patch(String(id), { closing: true });
};

toast.done = (id) => toast.dismiss(id);

toast.isActive = (id) => {
  const t = store.find(String(id));
  return !!t && !t.closing;
};

toast.update = (id, options = {}) => {
  const key = String(id);
  const existing = store.find(key);
  if (!existing) return;
  const nextType =
    options.type !== undefined
      ? options.type
      : options.isLoading === false && existing.type === TYPE.LOADING
      ? TYPE.DEFAULT
      : existing.type;
  const merged = {
    title: existing.title,
    position: existing.position,
    closeButton: options.closeButton !== undefined ? options.closeButton : nextType !== TYPE.LOADING,
    hideProgressBar: existing.hideProgressBar,
    pauseOnHover: existing.pauseOnHover,
    closeOnClick: options.closeOnClick !== undefined ? options.closeOnClick : nextType !== TYPE.LOADING,
    icon: existing.icon,
    className: existing.className,
    onClose: existing.onClose,
    action: existing.action,
    data: existing.data,
    ...options,
    type: nextType,
  };
  const spec = buildSpec(options.render !== undefined ? options.render : existing.content, nextType, merged);
  if (spec.content === null) spec.content = existing.content;
  store.patch(key, (t) => ({ ...spec, closing: false, nonce: t.nonce + 1, updatedAt: Date.now() }));
};

const resolveMessage = (msg, payload) => {
  if (typeof msg === "function") return msg(payload);
  if (msg && typeof msg === "object" && !React.isValidElement(msg) && "render" in msg) {
    return typeof msg.render === "function" ? msg.render({ data: payload }) : msg.render;
  }
  return msg;
};

toast.promise = (promiseOrFn, messages = {}, options = {}) => {
  const promise = typeof promiseOrFn === "function" ? promiseOrFn() : promiseOrFn;
  const id = show(messages.pending || "Working on it…", TYPE.LOADING, {
    closeButton: false,
    closeOnClick: false,
    ...options,
  });
  const settle = (type, msg, payload) => {
    const rendered = toRenderable(resolveMessage(msg, payload), type);
    if (!rendered || !id) {
      if (id) toast.dismiss(id);
      return;
    }
    toast.update(id, { render: rendered, type, isLoading: false, ...options, autoClose: options.autoClose });
  };
  promise.then(
    (value) => settle(TYPE.SUCCESS, messages.success, value),
    (reason) => settle(TYPE.ERROR, messages.error, reason)
  );
  return promise;
};

// No-ops kept for API parity with react-toastify.
toast.clearWaitingQueue = () => {};
toast.play = () => {};
toast.pause = () => {};
toast.onChange = () => () => {};

toast.TYPE = TYPE;
toast.POSITION = POSITION;

export { toast };
export default toast;
